/**
 * VerifyEmail.jsx — S1: page de vérification email.
 *
 * Lue depuis le lien envoyé par email : /auth/verify-email?token=XYZ
 * - Si token valide → l'API marque l'email comme vérifié.
 * - Si token invalide / expiré → message clair + bouton "Demander un nouveau lien".
 */
import React, { useEffect, useState } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Loader2, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { authAPI } from '../utils/api';

const VerifyEmail = () => {
  // BUG-005 — Per-route <title>
  usePageTitle('Vérifier votre adresse email | Realty237');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [state, setState] = useState('loading'); // loading | success | already | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) {
      setState('error');
      setErrorMsg('Lien invalide — aucun jeton fourni.');
      return;
    }
    authAPI
      .verifyEmail(token)
      .then((r) => {
        setState(r.data.already_verified ? 'already' : 'success');
      })
      .catch((err) => {
        setState('error');
        setErrorMsg(err.response?.data?.detail || 'Lien invalide ou expiré.');
      });
  }, [token]);

  return (
    <div className="min-h-dvh bg-gradient-to-br from-orange-50 via-white to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="verify-email-card">
        <CardContent className="p-8 text-center space-y-4">
          {state === 'loading' && (
            <>
              <Loader2 className="h-12 w-12 mx-auto text-orange-500 animate-spin" />
              <h1 className="text-xl font-bold text-slate-900">Vérification en cours…</h1>
            </>
          )}
          {state === 'success' && (
            <>
              <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-500" />
              <h1 className="text-2xl font-bold text-slate-900">
                Email vérifié !
              </h1>
              <p className="text-sm text-slate-600">
                Votre compte est entièrement actif. Vous pouvez maintenant
                publier vos annonces.
              </p>
              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white"
                data-testid="verify-email-go-dashboard"
              >
                <Home className="h-4 w-4 mr-1.5" />
                Aller à mon tableau de bord
              </Button>
            </>
          )}
          {state === 'already' && (
            <>
              <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-500" />
              <h1 className="text-xl font-bold text-slate-900">Déjà vérifié</h1>
              <p className="text-sm text-slate-600">
                Votre email était déjà confirmé.
              </p>
              <Button onClick={() => navigate('/dashboard')} className="w-full">
                Tableau de bord
              </Button>
            </>
          )}
          {state === 'error' && (
            <>
              <AlertCircle className="h-14 w-14 mx-auto text-red-500" />
              <h1 className="text-xl font-bold text-slate-900">Échec de la vérification</h1>
              <p className="text-sm text-slate-600">{errorMsg}</p>
              <p className="text-xs text-slate-500">
                Connectez-vous à votre compte et utilisez le bouton « Renvoyer
                l&apos;email » dans le bandeau du tableau de bord.
              </p>
              <Link to="/auth?mode=login">
                <Button variant="outline" className="w-full">
                  Se connecter
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
